import type { LastLevel1CapitalBMV } from "apis/models/lastLevel1CapitalBMV";
import { API_STOCKS_FIELDS } from "apis/constants";
import { MARKET_TYPES } from "constants/globals";
import { fetchNext } from "../utils/request";
import requestTypes from "../constants/requestTypes";
import type {
  GetInstrumentResponse,
  InstrumentObjectResponse
} from "./models/instrument";
import { getApiMarketUrl } from "../utils/endpoints";

const marketUrl = getApiMarketUrl();

export default class InstrumentApi {
  static getAllInstruments = (): Promise<GetInstrumentResponse> =>
    fetchNext(requestTypes.GET, `/instruments/`, undefined, marketUrl);

  static getInstrumentListByWidgetType = (
    widgetTypeId: string
  ): Promise<GetInstrumentResponse> =>
    fetchNext(
      requestTypes.GET,
      `/instruments/widgetType/${widgetTypeId}`,
      undefined,
      marketUrl
    );

  static getInstrumentData = (
    instruments: string
  ): Promise<{
    data: Array<LastLevel1CapitalBMV>
  }> =>
    fetchNext(
      requestTypes.GET,
      `/instruments/last?instrumentId=${instruments}&fields=${API_STOCKS_FIELDS}`,
      undefined,
      marketUrl
    );

  static getInstrumentDataWithDelay = (
    instruments: string
  ): Promise<{
    data: Array<LastLevel1CapitalBMV>
  }> =>
    fetchNext(
      requestTypes.GET,
      `/instruments/last?instrumentId=${instruments}&fields=${API_STOCKS_FIELDS}&informationOpportunity=0`,
      undefined,
      marketUrl
    );

  static getInstrumentDataTreeMap = (
    condition: string,
    marketType: string
  ): Promise<{
    data: Array<LastLevel1CapitalBMV>
  }> =>
    fetchNext(
      requestTypes.GET,
      // `importeTotal` viaja en la misma respuesta y no cuesta una petición
      // extra: es el área de respaldo del Treemap para los mercados sin
      // capitalización de mercado reportada (SIC). Ver `Treemap/utils.sizeRows`.
      `/instruments/last/dynamic?marketTypeId=1&condition=${condition}&value=porcentaje&limit=20&fields=descripcion,porcentaje,variacion,instrumentKey,importeTotal&valueTypeIds=${
        marketType === MARKET_TYPES.NATIONAL
          ? "12320,12576,12610,12611,12613,13088,13361,17222"
          : "12609"
      }`,
      undefined,
      marketUrl
    );

  static getInstrumentDataCapitalization = (
    instrumentKey: string
  ): Promise<{
    data: Array<LastLevel1CapitalBMV>
  }> =>
    fetchNext(
      requestTypes.GET,
      `/instruments/report/market/capitalization?instrumentId=${instrumentKey}`,
      undefined,
      marketUrl
    );

  static addInstrument = (
    workspaceId: string,
    widgetId: string,
    instrumentId: string,
    order: number
  ): Promise<InstrumentObjectResponse> => {
    const reqBody = {
      instrumentId,
      order
    };

    return fetchNext(
      requestTypes.POST,
      `/users/workspaces/${workspaceId}/widgets/${widgetId}/instrument`,
      reqBody
    );
  };

  static deleteInstrument = (
    workspaceId: string,
    widgetId: string,
    instrumentId: string,
    order: number
  ) => {
    const reqBody = {
      instrumentId,
      order
    };

    return fetchNext(
      requestTypes.DELETE,
      `/users/workspaces/${workspaceId}/widgets/${widgetId}/instrument`,
      reqBody
    );
  };

  static searchInstrumentsByMarketType = (
    marketTypeId: string,
    searchTerm: string,
    excludeSection: string = ""
  ): Promise<GetInstrumentResponse> => {
    return fetchNext(
      requestTypes.GET,
      `/instruments?searchQuery=${encodeURIComponent(
        searchTerm
      )}&markets=${encodeURIComponent(marketTypeId)}&limit=100${
        excludeSection ? "&excludeSection= " : ""
      }${encodeURIComponent(excludeSection)}`
    );
  };
}
